// AI 员工语音通话 Modal（缇娜 / 河伯 / 小李 共用，避免三处语音逻辑漂移）
// 录音 → 转 WAV → 上传 → 调 /voice/talk → 播放语音；与缇娜原内联实现完全一致的行为，仅 API base 与外观色不同
import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'antd';
import { PhoneOutlined, AudioOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// ===== 录音 blob → 16kHz 单声道 WAV（浏览器原生，无需服务器 ffmpeg） =====
function writeStr(view, off, str) { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); }
function audioBufferToWav(buffer) {
  const numCh = 1, sampleRate = buffer.sampleRate, numFrames = buffer.length;
  const bytesPerSample = 2, blockAlign = numCh * bytesPerSample, byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const v = new DataView(ab);
  writeStr(v, 0, 'RIFF'); v.setUint32(4, 36 + dataSize, true); writeStr(v, 8, 'WAVE');
  writeStr(v, 12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, numCh, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, byteRate, true); v.setUint16(32, blockAlign, true); v.setUint16(34, 16, true);
  writeStr(v, 36, 'data'); v.setUint32(40, dataSize, true);
  const data = buffer.getChannelData(0);
  let off = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return ab;
}
function blobToWav16k(blob) {
  return new Promise(async (resolve, reject) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const tmp = new AC();
      const buf = await blob.arrayBuffer();
      const audioBuffer = await tmp.decodeAudioData(buf);
      // 自检：如果源 buffer 已经是空数据（峰值=0），直接拒绝
      const ch = audioBuffer.getChannelData(0);
      let maxAbs = 0;
      for (let i = 0; i < ch.length; i++) { const a = Math.abs(ch[i]); if (a > maxAbs) maxAbs = a; }
      if (maxAbs < 0.001) {
        tmp.close().catch(() => {});
        return reject(new Error('录音数据为空（浏览器没采到声音），请检查麦克风权限或换个麦克风后重试'));
      }
      tmp.close().catch(() => {});
      const targetRate = 16000;
      const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(audioBuffer.duration * targetRate)), targetRate);
      const src = offline.createBufferSource();
      src.buffer = audioBuffer; src.connect(offline.destination); src.start(0);
      const rendered = await offline.startRendering();
      // 自检：重采样后的峰值也要 > 0
      const rch = rendered.getChannelData(0);
      let rmax = 0;
      for (let i = 0; i < rch.length; i++) { const a = Math.abs(rch[i]); if (a > rmax) rmax = a; }
      if (rmax < 0.001) {
        return reject(new Error('重采样后音频为空，可能是浏览器兼容问题，建议换 Chrome / Edge 重试'));
      }
      // 调试日志（控制台可看）：原始峰值、重采样峰值、时长
      if (typeof console !== 'undefined') console.log('[voice] blob=', blob.size, 'src 峰值=', maxAbs.toFixed(3), '重采样峰值=', rmax.toFixed(3), '时长=', audioBuffer.duration.toFixed(2), 's');
      resolve(new Blob([audioBufferToWav(rendered)], { type: 'audio/wav' }));
    } catch (e) { reject(e); }
  });
}

/**
 * 通用 AI 员工语音通话 Modal
 * Props:
 *   open           是否显示
 *   onClose        关闭回调（父组件控制 open）
 *   name           员工名字（标题显示）
 *   avatar         ReactNode（头部圆形头像）
 *   accentColor    主色（听音时麦克风背景 + "你说" 文字颜色）
 *   apiBase        后端 API 路径前缀，如 '/api/hebo'
 *   onResult       ({ recognized, reply, pending }) => void；pending 可能 undefined（河伯/小李）
 */
export default function VoiceCallModal({ open, onClose, name, avatar, accentColor, apiBase, onResult }) {
  const [callStatus, setCallStatus] = useState('idle'); // idle | listening | thinking | speaking
  const [callText, setCallText] = useState('');
  const [callReply, setCallReply] = useState('');
  const [callError, setCallError] = useState('');
  const callRef = useRef({
    active: false, stream: null, audioCtx: null, analyser: null,
    recorder: null, chunks: [], source: null, rafId: null,
    speaking: false, silenceStart: null, hasSpoken: false,
  });
  const playerRef = useRef(null);

  // 清理所有音频资源
  const cleanupAudio = () => {
    const r = callRef.current;
    r.active = false;
    if (r.rafId) { cancelAnimationFrame(r.rafId); r.rafId = null; }
    if (r.recorder && r.recorder.state !== 'inactive') { try { r.recorder.stop(); } catch (e) {} }
    if (r.source) { try { r.source.disconnect(); } catch (e) {} }
    if (r.audioCtx) { r.audioCtx.close().catch(() => {}); r.audioCtx = null; }
    if (r.stream) { r.stream.getTracks().forEach(t => t.stop()); r.stream = null; }
    if (playerRef.current) { try { playerRef.current.pause(); } catch (e) {} playerRef.current = null; }
    r.analyser = null;
    setCallStatus('idle');
  };

  // open 变化：开启录音 / 清理
  useEffect(() => {
    if (open) {
      setCallError(''); setCallText(''); setCallReply('');
      callRef.current.active = true;
      startListening();
    }
    return () => { if (open) cleanupAudio(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startListening = async () => {
    const r = callRef.current;
    try {
      // 麦克风（getUserMedia）只在安全连接（https 或 localhost）下可用；http 明文会被浏览器直接禁用且不弹权限
      if (!window.isSecureContext) {
        setCallError('当前是 http:// 明文连接，浏览器已禁用麦克风。请改用 https://www.ledou.cc 打开系统再试');
        setCallStatus('idle');
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCallError('当前浏览器不支持麦克风，请用最新版 Chrome / Edge 打开');
        setCallStatus('idle');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,    // 关掉回声消除：某些声卡下会导致录音静音
          noiseSuppression: false,    // 关掉噪声抑制：同上有概率吃掉人声
          autoGainControl: false,     // 关掉自动增益：低音量环境会被压成静音
        }
      });
      r.stream = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AC();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      r.audioCtx = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      r.source = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      r.analyser = analyser;

      let mime = '';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mime = 'audio/webm;codecs=opus';
      else if (MediaRecorder.isTypeSupported('audio/webm')) mime = 'audio/webm';
      else if (MediaRecorder.isTypeSupported('audio/mp4')) mime = 'audio/mp4';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      r.recorder = recorder;
      r.chunks = [];
      recorder.ondataavailable = e => { if (e.data && e.data.size) r.chunks.push(e.data); };
      recorder.onstop = () => { onRecordStop(); };
      recorder.start(200);

      r.speaking = false; r.silenceStart = null; r.hasSpoken = false;
      setCallStatus('listening');
      vadLoop();
    } catch (e) {
      const msg = (e && e.message) || '';
      let hint = '请检查浏览器麦克风权限';
      if (/notallow|permission|denied/i.test(msg)) hint = '麦克风权限被拒绝：请点地址栏左侧的锁图标，把「麦克风」设为允许后刷新';
      else if (/notfound|no audio|devices.*not|no.*device/i.test(msg)) hint = '未检测到麦克风设备，请检查麦克风是否已连接';
      else if (/notreadable|in use|concurrent/i.test(msg)) hint = '麦克风被其他应用占用，请关闭占用程序后重试';
      setCallError('无法使用麦克风：' + hint);
      setCallStatus('idle');
    }
  };

  const vadLoop = () => {
    const r = callRef.current;
    if (!r.active) return;
    const analyser = r.analyser;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / data.length);
    const now = Date.now();
    if (rms > 0.03) { r.speaking = true; r.hasSpoken = true; r.silenceStart = null; }
    else if (r.hasSpoken) {
      if (r.silenceStart == null) r.silenceStart = now;
      else if (now - r.silenceStart > 1200) { stopRecording(); return; }
    }
    r.rafId = requestAnimationFrame(vadLoop);
  };

  const stopRecording = () => {
    const r = callRef.current;
    if (r.rafId) { cancelAnimationFrame(r.rafId); r.rafId = null; }
    if (r.recorder && r.recorder.state !== 'inactive') { try { r.recorder.stop(); } catch (e) {} }
    if (r.source) { try { r.source.disconnect(); } catch (e) {} }
    if (r.audioCtx) { r.audioCtx.close().catch(() => {}); r.audioCtx = null; }
    if (r.stream) { r.stream.getTracks().forEach(t => t.stop()); r.stream = null; }
    r.analyser = null;
    setCallStatus('thinking');
  };

  const onRecordStop = async () => {
    const r = callRef.current;
    const chunks = r.chunks; r.chunks = [];
    if (!chunks.length) { if (r.active) startListening(); else setCallStatus('idle'); return; }
    const blob = new Blob(chunks, { type: (chunks[0] && chunks[0].type) || 'audio/webm' });
    if (blob.size < 2000) { if (r.active) startListening(); return; }
    try {
      const wavBlob = await blobToWav16k(blob);
      const fd = new FormData();
      fd.append('audio', wavBlob, 'voice.wav');
      const up = await apiFetch(apiBase + '/voice/upload', { method: 'POST', body: fd }).then(x => x.json());
      if (!up.success) throw new Error(up.error || '音频上传失败');
      const talk = await apiFetch(apiBase + '/voice/talk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioUrl: up.url }),
      }).then(x => x.json());
      if (!talk.success) throw new Error(talk.error || '对方没听清');
      setCallText(talk.recognized || '');
      setCallReply(talk.reply || '');
      setCallError('');
      try { if (onResult) onResult({ recognized: talk.recognized, reply: talk.reply, pending: talk.pending }); } catch (e) {}
      if (talk.audio) {
        setCallStatus('speaking');
        await playAudio(talk.audio);
      }
      if (r.active) startListening();
    } catch (e) {
      setCallError((e && e.message) || '通话出错，请重试');
      if (r.active) { await sleep(1000); startListening(); }
    }
  };

  const playAudio = (url) => new Promise((resolve) => {
    const a = new Audio(url);
    playerRef.current = a;
    a.onended = () => { playerRef.current = null; resolve(); };
    a.onerror = () => { playerRef.current = null; resolve(); };
    a.play().catch(() => { playerRef.current = null; resolve(); });
  });

  // 点击麦克风：正在听→立即结束说话并发送；正在说→打断播放、开始听
  const onMicClick = () => {
    if (callStatus === 'listening') stopRecording();
    else if (callStatus === 'speaking') {
      if (playerRef.current) { try { playerRef.current.pause(); } catch (e) {} playerRef.current = null; }
      startListening();
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      width={400}
      centered
      onCancel={onClose}
      bodyStyle={{ padding: 0, background: 'var(--brand-bg)', borderRadius: 16 }}
    >
      <div style={{ padding: '28px 22px 22px', textAlign: 'center' }}>
        {avatar}
        <div style={{ fontSize: 17, fontWeight: 600, color: '#26201C', marginTop: 10 }}>{name}</div>
        <div style={{ fontSize: 12.5, color: '#8c8c8c', marginTop: 3, minHeight: 20 }}>
          {callStatus === 'listening' ? '我在听，请说～' : callStatus === 'thinking' ? '让我查一查、想一想…' : callStatus === 'speaking' ? '我在说，你可以随时打断' : ''}
        </div>

        <div style={{ margin: '22px 0 6px' }}>
          <button
            onClick={onMicClick}
            className={callStatus === 'listening' ? 'tina-mic-pulse' : callStatus === 'speaking' ? 'tina-speaking' : ''}
            style={{
              width: 88, height: 88, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: callStatus === 'listening' ? accentColor : callStatus === 'speaking' ? '#07C160' : 'var(--brand-border)',
              color: '#fff', fontSize: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .2s',
              '--mic-glow': accentColor + '66',
            }}
          >
            {callStatus === 'speaking' ? <PhoneOutlined /> : <AudioOutlined />}
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#bfbfbf' }}>
          {callStatus === 'listening' ? '说完会自动识别，也可点一下立即发送' : callStatus === 'speaking' ? '点一下打断我' : ''}
        </div>

        {(callText || callReply) && (
          <div style={{ textAlign: 'left', background: '#fff', borderRadius: 12, padding: '12px 14px', marginTop: 16, maxHeight: 150, overflowY: 'auto', border: '0.5px solid var(--brand-border)' }}>
            {callText && <div style={{ fontSize: 12.5, color: accentColor, marginBottom: 6 }}>你说：{callText}</div>}
            {callReply && <div style={{ fontSize: 12.5, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{callReply}</div>}
          </div>
        )}

        {callError && <div style={{ color: 'var(--brand)', fontSize: 12.5, marginTop: 10 }}>{callError}</div>}

        <div style={{ marginTop: 22 }}>
          <button
            onClick={onClose}
            style={{
              width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: '#E5484D', color: '#fff', fontSize: 20,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <PhoneOutlined style={{ transform: 'rotate(135deg)' }} />
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 6 }}>挂断</div>
      </div>
    </Modal>
  );
}